import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/lib/auth';
import {
  lpr,
  type ClinicLprSettings,
  type LprDiagnose,
  type LprDiagnoseArt,
  type LprForloeb,
  type LprForloebStatus,
  type LprKontakt,
  type LprProcedure,
  type LprSksCode,
  type LprSubmission,
} from '@/types/lpr';

export type LprForloebListItem = LprForloeb & {
  patient_first_name: string;
  patient_last_name: string;
};

export const lprSksQueryKey = () => ['lpr', 'sks'] as const;
export const lprSettingsQueryKey = (clinicId: string | null) =>
  ['lpr', 'settings', clinicId] as const;
export const lprListQueryKey = (clinicId: string | null, status: string) =>
  ['lpr', 'list', clinicId, status] as const;
export const lprPatientListQueryKey = (clinicId: string | null, patientId: string | null) =>
  ['lpr', 'patient', clinicId, patientId] as const;
export const lprForloebQueryKey = (id: string | null | undefined) =>
  ['lpr', 'forloeb', id ?? null] as const;
export const lprKontakterQueryKey = (forloebId: string | null | undefined) =>
  ['lpr', 'kontakter', forloebId ?? null] as const;
export const lprSubmissionsQueryKey = (forloebId: string | null | undefined) =>
  ['lpr', 'submissions', forloebId ?? null] as const;

export function mapReferrerTypeToHenvisningsmaade(referrerType: string | null | undefined): string {
  switch (referrerType) {
    case 'egen_laege':
      return 'ALDA30';
    case 'speciallaege':
      return 'ALDA31';
    case 'selvhenvist':
      return 'ALDA00';
    default:
      return 'ALDA90';
  }
}

export function sksLabel(codes: LprSksCode[] | undefined, code: string | null | undefined, locale: string): string {
  if (!code) return '—';
  const row = codes?.find((c) => c.code === code);
  if (!row) return code;
  return `${row.code} ${locale.startsWith('en') ? row.label_en : row.label_da}`;
}

export function useLprSksCodes() {
  return useQuery({
    queryKey: lprSksQueryKey(),
    queryFn: async (): Promise<LprSksCode[]> => {
      const { data, error } = await lpr
        .from('lpr_sks_codes')
        .select('*')
        .eq('is_active', true)
        .order('sort_order');
      if (error) throw error;
      return (data ?? []) as LprSksCode[];
    },
    staleTime: 5 * 60_000,
  });
}

export function useLprSettings(clinicId: string | null | undefined) {
  return useQuery({
    queryKey: lprSettingsQueryKey(clinicId ?? null),
    enabled: Boolean(clinicId),
    queryFn: async (): Promise<ClinicLprSettings | null> => {
      if (!clinicId) return null;
      const { data, error } = await lpr
        .from('clinic_lpr_settings')
        .select('*')
        .eq('clinic_id', clinicId)
        .maybeSingle();
      if (error) throw error;
      return (data as ClinicLprSettings | null) ?? null;
    },
  });
}

export function useUpsertLprSettings(clinicId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch: Partial<ClinicLprSettings>): Promise<ClinicLprSettings> => {
      if (!clinicId) throw new Error('No clinic');
      const { data, error } = await lpr
        .from('clinic_lpr_settings')
        .upsert({ clinic_id: clinicId, ...patch }, { onConflict: 'clinic_id' })
        .select('*')
        .single();
      if (error) throw error;
      return data as ClinicLprSettings;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: lprSettingsQueryKey(clinicId ?? null) });
    },
  });
}

export function useLprForloebList(
  clinicId: string | null | undefined,
  status: LprForloebStatus | 'all',
) {
  return useQuery({
    queryKey: lprListQueryKey(clinicId ?? null, status),
    enabled: Boolean(clinicId),
    queryFn: async (): Promise<LprForloebListItem[]> => {
      if (!clinicId) return [];
      let query = lpr
        .from('lpr_forloeb')
        .select('*, patient:patients(first_name,last_name)')
        .eq('clinic_id', clinicId)
        .order('start_at', { ascending: false });
      if (status !== 'all') query = query.eq('status', status);
      const { data, error } = await query;
      if (error) throw error;
      return ((data ?? []) as Array<LprForloeb & { patient: { first_name: string; last_name: string } | null }>).map(
        (row) => ({
          ...row,
          patient_first_name: row.patient?.first_name ?? '',
          patient_last_name: row.patient?.last_name ?? '',
        }),
      );
    },
  });
}

export function useLprForloebForPatient(
  clinicId: string | null | undefined,
  patientId: string | null | undefined,
) {
  return useQuery({
    queryKey: lprPatientListQueryKey(clinicId ?? null, patientId ?? null),
    enabled: Boolean(clinicId && patientId),
    queryFn: async (): Promise<LprForloeb[]> => {
      if (!clinicId || !patientId) return [];
      const { data, error } = await lpr
        .from('lpr_forloeb')
        .select('*')
        .eq('clinic_id', clinicId)
        .eq('patient_id', patientId)
        .order('start_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as LprForloeb[];
    },
  });
}

export function useLprForloeb(id: string | null | undefined) {
  return useQuery({
    queryKey: lprForloebQueryKey(id),
    enabled: Boolean(id),
    queryFn: async (): Promise<LprForloeb | null> => {
      if (!id) return null;
      const { data, error } = await lpr.from('lpr_forloeb').select('*').eq('id', id).maybeSingle();
      if (error) throw error;
      return (data as LprForloeb | null) ?? null;
    },
  });
}

export function useLprKontakter(forloebId: string | null | undefined) {
  return useQuery({
    queryKey: lprKontakterQueryKey(forloebId),
    enabled: Boolean(forloebId),
    queryFn: async (): Promise<{
      kontakter: LprKontakt[];
      diagnoser: LprDiagnose[];
      procedurer: LprProcedure[];
    }> => {
      if (!forloebId) return { kontakter: [], diagnoser: [], procedurer: [] };
      const { data: kontakter, error: kErr } = await lpr
        .from('lpr_kontakter')
        .select('*')
        .eq('forloeb_id', forloebId)
        .order('start_at', { ascending: true });
      if (kErr) throw kErr;
      const rows = (kontakter ?? []) as LprKontakt[];
      const ids = rows.map((k) => k.id);
      if (ids.length === 0) return { kontakter: rows, diagnoser: [], procedurer: [] };

      const [{ data: diagnoser, error: dErr }, { data: procedurer, error: pErr }] = await Promise.all([
        lpr.from('lpr_diagnoser').select('*').in('kontakt_id', ids),
        lpr.from('lpr_procedurer').select('*').in('kontakt_id', ids),
      ]);
      if (dErr) throw dErr;
      if (pErr) throw pErr;
      return {
        kontakter: rows,
        diagnoser: (diagnoser ?? []) as LprDiagnose[],
        procedurer: (procedurer ?? []) as LprProcedure[],
      };
    },
  });
}

export function useLprSubmissions(forloebId: string | null | undefined) {
  return useQuery({
    queryKey: lprSubmissionsQueryKey(forloebId),
    enabled: Boolean(forloebId),
    queryFn: async (): Promise<LprSubmission[]> => {
      if (!forloebId) return [];
      const { data, error } = await lpr
        .from('lpr_submissions')
        .select('*')
        .eq('forloeb_id', forloebId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as LprSubmission[];
    },
  });
}

async function resolveClinicSor(clinicId: string, explicit?: string | null): Promise<string | null> {
  if (explicit) return explicit;
  const { data: settings } = await lpr
    .from('clinic_lpr_settings')
    .select('sor_id')
    .eq('clinic_id', clinicId)
    .maybeSingle();
  if (settings?.sor_id) return settings.sor_id;
  const { data: communicate } = await lpr
    .from('clinic_communicate_connections')
    .select('sor_id')
    .eq('clinic_id', clinicId)
    .maybeSingle();
  return communicate?.sor_id ?? null;
}

async function defaultSettings(clinicId: string): Promise<Partial<ClinicLprSettings>> {
  const { data } = await lpr
    .from('clinic_lpr_settings')
    .select('*')
    .eq('clinic_id', clinicId)
    .maybeSingle();
  return (data as ClinicLprSettings | null) ?? {};
}

export interface CreateLprForloebInput {
  clinic_id: string;
  patient_id: string;
  referral_id?: string | null;
  henvisningstidspunkt?: string;
  henvisningsmaade?: string;
  frit_valg?: string;
  forloebslabel?: string;
  start_at?: string;
  importAppointments?: boolean;
}

export function useCreateLprForloeb() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (input: CreateLprForloebInput): Promise<LprForloeb> => {
      if (input.referral_id) {
        const { data: existing } = await lpr
          .from('lpr_forloeb')
          .select('*')
          .eq('referral_id', input.referral_id)
          .maybeSingle();
        if (existing) return existing as LprForloeb;
      }

      const defaults = await defaultSettings(input.clinic_id);
      const sorId = await resolveClinicSor(input.clinic_id, defaults.sor_id);
      const startAt = input.start_at ?? input.henvisningstidspunkt ?? new Date().toISOString();

      const { data, error } = await lpr
        .from('lpr_forloeb')
        .insert({
          clinic_id: input.clinic_id,
          patient_id: input.patient_id,
          referral_id: input.referral_id ?? null,
          sor_id: sorId,
          start_at: startAt,
          henvisningstidspunkt: input.henvisningstidspunkt ?? startAt,
          henvisningsmaade:
            input.henvisningsmaade ?? defaults.default_henvisningsmaade ?? 'ALDA30',
          frit_valg: input.frit_valg ?? defaults.default_frit_valg ?? 'ALDB00',
          forloebslabel: input.forloebslabel ?? defaults.default_forloebslabel ?? 'ALAL90',
          created_by_user_id: user?.id ?? null,
        })
        .select('*')
        .single();
      if (error) throw error;
      const forloeb = data as LprForloeb;

      if (input.importAppointments !== false) {
        await importAppointmentsAsKontakter(forloeb, defaults);
      }
      return forloeb;
    },
    onSuccess: (row) => {
      void qc.invalidateQueries({ queryKey: ['lpr'] });
      void qc.invalidateQueries({ queryKey: lprForloebQueryKey(row.id) });
    },
  });
}

async function importAppointmentsAsKontakter(
  forloeb: LprForloeb,
  defaults: Partial<ClinicLprSettings>,
): Promise<number> {
  const { data: appts, error } = await lpr
    .from('appointments')
    .select('id, starts_at, ends_at, status, service_name, notes, video_meeting_url')
    .eq('clinic_id', forloeb.clinic_id)
    .eq('patient_id', forloeb.patient_id)
    .neq('status', 'cancelled')
    .order('starts_at', { ascending: true });
  if (error) throw error;
  if (!appts?.length) return 0;

  type ApptRow = {
    id: string;
    starts_at: string;
    ends_at: string;
    status: string;
    service_name: string | null;
    notes: string | null;
    video_meeting_url: string | null;
  };
  const appointments = appts as ApptRow[];

  const { data: existing } = await lpr
    .from('lpr_kontakter')
    .select('appointment_id')
    .eq('forloeb_id', forloeb.id)
    .not('appointment_id', 'is', null);
  const used = new Set(
    ((existing ?? []) as Array<{ appointment_id: string | null }>).map((r) => r.appointment_id),
  );

  const rows = appointments
    .filter((a) => a.id && !used.has(a.id))
    .map((a) => ({
      clinic_id: forloeb.clinic_id,
      forloeb_id: forloeb.id,
      appointment_id: a.id,
      kontakt_type: a.video_meeting_url
        ? 'ALCA03'
        : (defaults.default_kontakt_type ?? 'ALCA00'),
      start_at: a.starts_at,
      end_at: a.ends_at,
      ansvarlig_sor: forloeb.sor_id,
      betaling: defaults.default_betaling ?? 'ALFA1',
      notes: [a.service_name, a.notes].filter(Boolean).join(' — ') || null,
    }));
  if (rows.length === 0) return 0;
  const { error: insErr } = await lpr.from('lpr_kontakter').insert(rows);
  if (insErr) throw insErr;
  return rows.length;
}

export function useImportLprAppointments(forloebId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<number> => {
      if (!forloebId) throw new Error('Missing forløb');
      const { data: forloeb, error } = await lpr
        .from('lpr_forloeb')
        .select('*')
        .eq('id', forloebId)
        .single();
      if (error) throw error;
      const defaults = await defaultSettings(forloeb.clinic_id);
      return importAppointmentsAsKontakter(forloeb as LprForloeb, defaults);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: lprKontakterQueryKey(forloebId) });
    },
  });
}

export function useUpdateLprForloeb(forloebId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch: Partial<LprForloeb>): Promise<LprForloeb> => {
      if (!forloebId) throw new Error('Missing forløb');
      const { data, error } = await lpr
        .from('lpr_forloeb')
        .update(patch)
        .eq('id', forloebId)
        .select('*')
        .single();
      if (error) throw error;
      return data as LprForloeb;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['lpr'] });
    },
  });
}

export function useCreateLprKontakt(forloeb: LprForloeb | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      start_at: string;
      end_at: string;
      kontakt_type?: string;
      betaling?: string;
      notes?: string | null;
    }): Promise<LprKontakt> => {
      if (!forloeb) throw new Error('Missing forløb');
      const { data, error } = await lpr
        .from('lpr_kontakter')
        .insert({
          clinic_id: forloeb.clinic_id,
          forloeb_id: forloeb.id,
          start_at: input.start_at,
          end_at: input.end_at,
          kontakt_type: input.kontakt_type ?? 'ALCA00',
          betaling: input.betaling ?? 'ALFA1',
          ansvarlig_sor: forloeb.sor_id,
          notes: input.notes ?? null,
        })
        .select('*')
        .single();
      if (error) throw error;
      return data as LprKontakt;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: lprKontakterQueryKey(forloeb?.id) });
    },
  });
}

export function useUpdateLprKontakt(forloebId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string } & Partial<LprKontakt>): Promise<void> => {
      const { id, ...patch } = input;
      const { error } = await lpr.from('lpr_kontakter').update(patch).eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: lprKontakterQueryKey(forloebId) });
    },
  });
}

export function useDeleteLprKontakt(forloebId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const { error } = await lpr.from('lpr_kontakter').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: lprKontakterQueryKey(forloebId) });
    },
  });
}

export function useAddLprDiagnose(forloebId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      clinic_id: string;
      kontakt_id: string;
      art: LprDiagnoseArt;
      sks_code: string;
      sks_text?: string | null;
    }): Promise<void> => {
      const { error } = await lpr.from('lpr_diagnoser').insert(input);
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: lprKontakterQueryKey(forloebId) });
    },
  });
}

export function useDeleteLprDiagnose(forloebId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const { error } = await lpr.from('lpr_diagnoser').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: lprKontakterQueryKey(forloebId) });
    },
  });
}

export function useAddLprProcedure(forloebId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      clinic_id: string;
      kontakt_id: string;
      sks_code: string;
      sks_text?: string | null;
      performed_at?: string | null;
    }): Promise<void> => {
      const { error } = await lpr.from('lpr_procedurer').insert(input);
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: lprKontakterQueryKey(forloebId) });
    },
  });
}

export function useDeleteLprProcedure(forloebId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const { error } = await lpr.from('lpr_procedurer').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: lprKontakterQueryKey(forloebId) });
    },
  });
}

export interface LprReportBlocker {
  key: string;
}

export function lprReportBlockers(
  forloeb: LprForloeb | null,
  kontakter: LprKontakt[],
  diagnoser: LprDiagnose[],
): LprReportBlocker[] {
  const blockers: LprReportBlocker[] = [];
  if (!forloeb) return [{ key: 'missing_forloeb' }];
  if (!forloeb.sor_id) blockers.push({ key: 'missing_sor' });
  if (!forloeb.henvisningstidspunkt) blockers.push({ key: 'missing_henvisningstidspunkt' });
  if (!forloeb.henvisningsmaade) blockers.push({ key: 'missing_henvisningsmaade' });
  if (!forloeb.frit_valg) blockers.push({ key: 'missing_frit_valg' });
  if (!forloeb.forloebslabel) blockers.push({ key: 'missing_forloebslabel' });
  if (kontakter.length === 0) blockers.push({ key: 'missing_kontakt' });
  const aktionByKontakt = new Set(diagnoser.filter((d) => d.art === 'aktion').map((d) => d.kontakt_id));
  if (kontakter.some((k) => !aktionByKontakt.has(k.id))) {
    blockers.push({ key: 'missing_aktionsdiagnose' });
  }
  return blockers;
}

export function useReportLprForloeb(forloebId: string | null | undefined) {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (): Promise<LprSubmission> => {
      if (!forloebId) throw new Error('Missing forløb');
      const { data: forloeb, error: fErr } = await lpr
        .from('lpr_forloeb')
        .select('*')
        .eq('id', forloebId)
        .single();
      if (fErr) throw fErr;
      const packed = await lpr
        .from('lpr_kontakter')
        .select('*')
        .eq('forloeb_id', forloebId)
        .order('start_at', { ascending: true });
      if (packed.error) throw packed.error;
      const kontakter = (packed.data ?? []) as LprKontakt[];
      const ids = kontakter.map((k) => k.id);
      const [{ data: diagnoser }, { data: procedurer }, { data: prev }] = await Promise.all([
        ids.length
          ? lpr.from('lpr_diagnoser').select('*').in('kontakt_id', ids)
          : Promise.resolve({ data: [] as LprDiagnose[] }),
        ids.length
          ? lpr.from('lpr_procedurer').select('*').in('kontakt_id', ids)
          : Promise.resolve({ data: [] as LprProcedure[] }),
        lpr
          .from('lpr_submissions')
          .select('version_number')
          .eq('forloeb_id', forloebId)
          .order('version_number', { ascending: false })
          .limit(1)
          .maybeSingle(),
      ]);

      const blockers = lprReportBlockers(
        forloeb as LprForloeb,
        kontakter,
        (diagnoser ?? []) as LprDiagnose[],
      );
      if (blockers.length > 0) {
        throw new Error(blockers.map((b) => b.key).join(','));
      }

      const version = (prev?.version_number ?? 0) + 1;
      const payload = {
        interface: 'LPR3-CDA-ready',
        mode: 'simulated',
        version,
        setId: (forloeb as LprForloeb).object_id,
        reportedAt: new Date().toISOString(),
        forloeb,
        kontakter: kontakter.map((k) => ({
          ...k,
          diagnoser: ((diagnoser ?? []) as LprDiagnose[]).filter((d) => d.kontakt_id === k.id),
          procedurer: ((procedurer ?? []) as LprProcedure[]).filter((p) => p.kontakt_id === k.id),
        })),
      };

      const { data: submission, error: sErr } = await lpr
        .from('lpr_submissions')
        .insert({
          clinic_id: (forloeb as LprForloeb).clinic_id,
          forloeb_id: forloebId,
          version_number: version,
          status: 'simulated',
          payload,
          created_by_user_id: user?.id ?? null,
        })
        .select('*')
        .single();
      if (sErr) throw sErr;

      const { error: uErr } = await lpr
        .from('lpr_forloeb')
        .update({ status: 'reported' })
        .eq('id', forloebId);
      if (uErr) throw uErr;

      return submission as LprSubmission;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['lpr'] });
    },
  });
}
